import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { ProColumns, ProTable, ActionType } from "@ant-design/pro-components";
import { Space, App, Button, Modal, Popconfirm } from "antd";

import { roleLabels } from "../../../constant/enum";
import { getBranchList } from "../../../general/api/branch.api";
import { IBranch } from "../../../general/type/branch.types";
import { removeEmptyParams } from "../../../utils/listQueryHelper";
import { capitalizeEachWord } from "../../../utils/stringUtils";
import { waitTime } from "../../../utils/WaitTime";
import { archiveUser, getUsers, editUser } from "../api/userAPI";
import { IUserResponse, IUserRequest } from "../type/user";

export const UserTable = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const actionRef = useRef<ActionType>();
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [confirmVerifyLoading, setConfirmVerifyLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUserResponse | null>(null);

  const handleVerifyUser = async () => {
    if (!selectedUser) return;

    try {
      setConfirmVerifyLoading(true);
      await editUser(selectedUser.id, {
        isEmailVerified: true,
      } as IUserRequest);
      message.success("User verified successfully");
      setIsVerifyModalOpen(false);
      actionRef.current?.reload();
    } catch (error: any) {
      message.error(error.message || "Failed to verify user");
    } finally {
      setConfirmVerifyLoading(false);
      setSelectedUser(null);
    }
  };

  const showModal = (record: IUserResponse) => {
    setSelectedUser(record);
    setIsVerifyModalOpen(true);
  };

  const handleCancel = () => {
    setIsVerifyModalOpen(false);
    setSelectedUser(null);
  };

  const columns: ProColumns<IUserResponse>[] = [
    {
      title: "No",
      dataIndex: "id",
      key: "id",
      search: false,
      render: (_, __, index) => <span>{index + 1}</span>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_, record) => capitalizeEachWord(record.name),
    },
    {
      title: "IC Number",
      dataIndex: "icNumber",
      key: "icNumber",
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      search: false,
      render: (_, record) => record.address ?? "-",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Contact No",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      valueType: "select",
      valueEnum: roleLabels,
      render: (_, record) => capitalizeEachWord(record.role),
    },
    {
      title: "Branch",
      dataIndex: "branch",
      key: "branchId",
      valueType: "select",
      render: (_, record) => record.branch?.branchName ?? "-",
      request: async () => {
        try {
          const branchData = await getBranchList();
          return (branchData.results || []).map((branch: IBranch) => ({
            label: branch.branchName,
            value: branch.id,
          }));
        } catch (error) {
          return [];
        }
      },
      fieldProps: {
        showSearch: true,
        placeholder: "Search by Branch Name",
        filterOption: (
          input: string,
          option?: { label: string; value: string }
        ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()),
      },
    },
    {
      title: "Verified Email",
      dataIndex: "isEmailVerified",
      key: "isEmailVerified",
      valueType: "select",
      render: (_, record) => (record.isEmailVerified ? "Yes" : "No"),
      fieldProps: {
        showSearch: true,
        placeholder: "Search by Verified",
        filterOption: (
          input: string,
          option?: { label: string; value: string }
        ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()),
        options: [
          { label: "Yes", value: true },
          { label: "No", value: false },
        ],
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      search: false,
      render: (_, record) => (
        <Space>
          <EditOutlined
            style={{ color: "blue" }}
            onClick={() =>
              navigate(`/users/${record.id}`, {
                state: { userId: record.id, displayName: record.name },
              })
            }
          />
          <Popconfirm
            title="Are you sure you want to delete this user?"
            description="This action cannot be undone."
            onConfirm={async () => {
              try {
                const response = await archiveUser(record.id);
                message.success(response.message);
                actionRef.current?.reload();
              } catch (error: any) {
                message.error(error.message);
              }
            }}
          >
            <DeleteOutlined style={{ color: "red" }} />
          </Popconfirm>
          {!record.isEmailVerified && (
            <Button type="primary" onClick={() => showModal(record)}>
              Verify
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <ProTable<IUserResponse>
        cardBordered
        form={{ layout: "vertical" }}
        scroll={{ x: "max-content" }}
        request={async (params, sort, filter) => {
          try {
            params = removeEmptyParams(params);
            const data = await getUsers({ filter, sort, params });
            const users = data.results;

            return {
              data: users,
              success: true,
              total: data.totalResults,
            };
          } catch (error) {
            return {
              data: [],
              success: false,
              total: 10,
            };
          }
        }}
        rowKey={(data) => data.id}
        columns={columns}
        editable={{
          type: "multiple",
          onSave: async (_rowKey, _data, _row) => {
            await waitTime(2000);
          },
        }}
        actionRef={actionRef}
      />
      <Modal
        title="Verify User"
        open={isVerifyModalOpen}
        onOk={handleVerifyUser}
        onCancel={handleCancel}
        confirmLoading={confirmVerifyLoading}
      >
        <p>Are you sure you want to verify {selectedUser?.name}?</p>
      </Modal>
    </>
  );
};
